import { formatDistanceToNowStrict } from 'date-fns';
import dateLanguages from 'src/utilities/dateFnsLocales';
// @mui
import { styled } from '@mui/material/styles';
import {
  Box,
  Avatar,
  ListItemText,
  ListItemAvatar,
  ListItemButton,
  Tooltip,
} from '@mui/material';
//
import BadgeStatus from 'src/minimals/components/BadgeStatus';
import { useSelector } from 'react-redux';
import ConversationLastMessage from './ConversationLastMessage';
import i18n from 'src/i18n';

const AVATAR_SIZE = 48;
const AVATAR_SIZE_GROUP = 32;

const getDetails = (conversation, currentUserId) => {
  const otherParticipants = conversation.participants.filter(
    (participant) => participant.id !== currentUserId
  );

  if (otherParticipants.length === 0)
    return {
      otherParticipants: [],
      displayNames: [i18n.t('chat.unknown_user_name')],
      lastMessage: {},
    };

  const displayNames = otherParticipants
    .reduce((names, participant) => [...names, participant?.name], [])
    .join(', ');

  const lastMessage = conversation.messages[conversation.messages.length - 1];

  return { otherParticipants, displayNames, lastMessage };
};

export default function ChatConversationItem({
  isSelected,
  conversation,
  isOpenSidebar,
  onSelectConversation,
}) {
  const { id: userId } = useSelector((state) => state.user);
  const { selectedLang } = useSelector((state) => state.language);
  const details = getDetails(conversation, userId);
  const isGroup = details.otherParticipants.length > 1;
  const isUnread = conversation.unread;
  const isOnlineGroup =
    isGroup &&
    details.otherParticipants.map((item) => item.status).includes('online');

  return (
    <Tooltip title={details.displayNames} placement="right" arrow>
      <RootStyle
        onClick={onSelectConversation}
        sx={{
          ...(isSelected && { bgcolor: 'action.selected' }),
        }}
      >
        <ListItemAvatar>
          <Box
            sx={{
              ...(isGroup && {
                position: 'relative',
                width: AVATAR_SIZE,
                height: AVATAR_SIZE,
                '& .avatarWrapper': {
                  position: 'absolute',
                  width: AVATAR_SIZE_GROUP,
                  height: AVATAR_SIZE_GROUP,
                  '&:nth-of-type(1)': {
                    left: 0,
                    zIndex: 9,
                    bottom: 2,
                    '& .MuiAvatar-root': {
                      border: (theme) =>
                        `solid 2px ${theme.palette.background.paper}`,
                    },
                  },
                  '&:nth-of-type(2)': { top: 2, right: 0 },
                },
              }),
            }}
          >
            {details.otherParticipants.slice(0, 2).map((participant) => (
              <AvatarWrapperStyle
                className="avatarWrapper"
                key={participant.id}
              >
                <Avatar
                  alt={participant?.name}
                  src={participant?.profilePicture}
                />
                {!isGroup && (
                  <BadgeStatus
                    status={participant?.status}
                    sx={{ right: 2, bottom: 2, position: 'absolute' }}
                  />
                )}
              </AvatarWrapperStyle>
            ))}
            {details.otherParticipants.length === 0 && <Avatar />}

            {isOnlineGroup && (
              <BadgeStatus
                status="online"
                sx={{ right: 2, bottom: 2, position: 'absolute' }}
              />
            )}
          </Box>
        </ListItemAvatar>

        {isOpenSidebar && (
          <>
            <ListItemText
              primary={details.displayNames}
              primaryTypographyProps={{
                noWrap: true,
                variant: 'subtitle2',
              }}
              secondary={
                <ConversationLastMessage
                  message={details.lastMessage}
                  currentUserId={userId}
                />
              }
              secondaryTypographyProps={{
                noWrap: true,
                variant: isUnread ? 'subtitle2' : 'body2',
                color: isUnread ? 'textPrimary' : 'textSecondary',
              }}
            />

            <Box
              sx={{
                ml: 2,
                height: 44,
                display: 'flex',
                alignItems: 'flex-end',
                flexDirection: 'column',
              }}
            >
              {details.lastMessage && (
                <Box
                  sx={{
                    mb: 1.25,
                    fontSize: 12,
                    lineHeight: '22px',
                    whiteSpace: 'nowrap',
                    color: 'text.disabled',
                  }}
                >
                  {formatDistanceToNowStrict(
                    new Date(details.lastMessage.createdAt),
                    {
                      addSuffix: false,
                      locale: dateLanguages[selectedLang],
                    }
                  )}
                </Box>
              )}
              {isUnread && <BadgeStatus status="unread" size="small" />}
            </Box>
          </>
        )}
      </RootStyle>
    </Tooltip>
  );
}

const RootStyle = styled(ListItemButton)(({ theme }) => ({
  padding: theme.spacing(1.5, 3),
  transition: theme.transitions.create('all'),
}));

const AvatarWrapperStyle = styled('div')({
  position: 'relative',
  width: AVATAR_SIZE,
  height: AVATAR_SIZE,
  '& .MuiAvatar-img': { borderRadius: '50%' },
  '& .MuiAvatar-root': { width: '100%', height: '100%' },
});
